import { useQuery } from '@apollo/client';
import { DonorStatus, UserRole, useGetUsersListQuery } from 'gql/types';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { GET_APP_SETTINGS_QUERY } from '../../../settings/graphql/queries';
import { DateRangeType } from '../../types/phlebotomist-dashboard';
import {
  DEFAULT_ATTN_SCORE_THRESHOLD,
  DEFAULT_ATTN_SCORE_WEIGHTS,
  DEFAULT_HEALTH_FLAG_THRESHOLDS,
  IAttnScoreWeights,
  IHealthFlagRange,
  IHealthFlagThresholds,
} from '../../utils/healthFlags';

/* ─── Recruiter filter URL sync ─── */

const VALID_DATE_RANGES = new Set<string>([
  'day',
  'yesterday',
  'week',
  'lastWeek',
  'month',
  'lastMonth',
  'quarter',
  'lastQuarter',
  'year',
  'lastYear',
  'dayCal',
  'yesterdayCal',
  'weekCal',
  'lastWeekCal',
  'monthCal',
  'lastMonthCal',
  'quarterCal',
  'lastQuarterCal',
  'yearCal',
  'pYearCal',
  'custom',
]);

const DEFAULT_RECRUITER_DATE_RANGE: DateRangeType = 'lastMonthCal';

interface IRecruiterFilters {
  dateRange: DateRangeType;
  customDateRange?: { start: Date; end: Date };
  regionId?: string;
  recruiterId?: string;
}

export interface IOverviewFilters {
  dateRange: DateRangeType;
  customDateRange?: { start: Date; end: Date };
  regionId?: string;
}

function recruiterFiltersFromUrl(
  searchParams: URLSearchParams
): IRecruiterFilters {
  const base: IRecruiterFilters = { dateRange: DEFAULT_RECRUITER_DATE_RANGE };
  const dr = searchParams.get('dateRange');
  if (dr && VALID_DATE_RANGES.has(dr)) base.dateRange = dr as DateRangeType;
  if (base.dateRange === 'custom') {
    const cs = searchParams.get('customStart');
    const ce = searchParams.get('customEnd');
    if (cs && ce) {
      const start = new Date(cs);
      const end = new Date(ce);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        base.customDateRange = { start, end };
      }
    }
  }
  const regionId = searchParams.get('regionId');
  if (regionId) base.regionId = regionId;
  const recruiterId = searchParams.get('recruiterId');
  if (recruiterId) base.recruiterId = recruiterId;
  return base;
}

function recruiterFiltersToUrl(filters: IRecruiterFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.dateRange !== DEFAULT_RECRUITER_DATE_RANGE) {
    params.set('dateRange', filters.dateRange);
  }
  if (filters.dateRange === 'custom' && filters.customDateRange) {
    params.set('customStart', filters.customDateRange.start.toISOString());
    params.set('customEnd', filters.customDateRange.end.toISOString());
  }
  if (filters.regionId) params.set('regionId', filters.regionId);
  if (filters.recruiterId) params.set('recruiterId', filters.recruiterId);
  return params;
}

/* ─── Types ─── */

export interface IOpsDashboardSettings {
  showDebug: boolean;
  simulateRole: UserRole | null;
  simulateUserId: string | null;
  tabVisibility: Record<
    string,
    Partial<Record<UserRole, 'all' | 'lock_region' | 'lock_office' | 'hidden'>>
  >;
  regionLocations: Record<string, string>;
  healthFlagThresholds: IHealthFlagThresholds;
  activeDonorStatuses: DonorStatus[];
  lastDonationActiveTimeoutDays: number;
  sglThresholdMl: number;
  activeDonorGrowthGoal: IHealthFlagRange;
  locationActiveGrowthGoal: IHealthFlagRange;
  locationPendingThreshold: IHealthFlagRange;
  attnScoreThreshold: IHealthFlagRange;
  attnScoreWeights: IAttnScoreWeights;
  locationUnitsDays: number;
  unitsPerEventDays: number;
  wcPrbcProductIds: number[];
  wcFfpSglProductIds: number[];
  wcFfpDblProductIds: number[];
  wcCryoProductIds: number[];
  wcMedicalSupplyProductIds: number[];
  wcRegionLocationMap: Record<string, string>;
}

const DEFAULT_ACTIVE_DONOR_STATUSES: DonorStatus[] = [
  DonorStatus.Active,
  DonorStatus.Approved,
  DonorStatus.AnnualLabworkDue,
];

const DEFAULT_OPS_SETTINGS: IOpsDashboardSettings = {
  showDebug: false,
  simulateRole: null,
  simulateUserId: null,
  tabVisibility: {},
  regionLocations: {},
  healthFlagThresholds: DEFAULT_HEALTH_FLAG_THRESHOLDS,
  activeDonorStatuses: DEFAULT_ACTIVE_DONOR_STATUSES,
  lastDonationActiveTimeoutDays: 90,
  sglThresholdMl: 420,
  activeDonorGrowthGoal: {
    downFrom: 1,
    downTo: -10,
    warnFrom: 1,
    warnTo: 10,
    upFrom: 20,
    upTo: 10,
  },
  locationActiveGrowthGoal: {
    downFrom: 0,
    downTo: -10,
    warnFrom: 1,
    warnTo: 9,
    upFrom: 20,
    upTo: 10,
  },
  locationPendingThreshold: {
    upFrom: 0,
    upTo: 2,
    warnFrom: 3,
    warnTo: 5,
    downFrom: 6,
    downTo: 10,
  },
  attnScoreThreshold: DEFAULT_ATTN_SCORE_THRESHOLD,
  attnScoreWeights: DEFAULT_ATTN_SCORE_WEIGHTS,
  locationUnitsDays: 29,
  unitsPerEventDays: 90,
  wcPrbcProductIds: [411, 463],
  wcFfpSglProductIds: [502, 8179],
  wcFfpDblProductIds: [503, 504],
  wcCryoProductIds: [15186, 15874],
  wcMedicalSupplyProductIds: [764],
  wcRegionLocationMap: {},
};

interface ISimulationInfo {
  role: UserRole;
  userName?: string;
  officeName?: string;
}

interface IOperationsDashboardContextValue {
  opsSettings: IOpsDashboardSettings;
  opsLoading: boolean;
  phlebotomistSettings: Record<string, unknown> | null;
  phlebotomistLoading: boolean;
  recruiterSettings: Record<string, unknown> | null;
  recruiterLoading: boolean;
  handlerSettings: Record<string, unknown> | null;
  handlerLoading: boolean;
  isSimulating: boolean;
  simulationInfo: ISimulationInfo | undefined;
  effectiveRole: UserRole | undefined;
  isDarkMode: boolean;
  toggleTheme: () => void;
  recruiterFilters: IRecruiterFilters;
  setRecruiterDateRange: (
    dateRange: DateRangeType,
    customRange?: { start: Date; end: Date }
  ) => void;
  setRecruiterRegionId: (regionId: string | undefined) => void;
  setRecruiterId: (recruiterId: string | undefined) => void;
  overviewFilters: IOverviewFilters;
  setOverviewDateRange: (
    dateRange: DateRangeType,
    customRange?: { start: Date; end: Date }
  ) => void;
  setOverviewRegionId: (regionId: string | undefined) => void;
  lastDrilldownUrl: string;
  setLastDrilldownUrl: (url: string) => void;
  containerWidth: number | null;
  setContainerWidth: (width: number) => void;
}

const OperationsDashboardContext =
  createContext<IOperationsDashboardContextValue>({
    opsSettings: DEFAULT_OPS_SETTINGS,
    opsLoading: true,
    phlebotomistSettings: null,
    phlebotomistLoading: true,
    recruiterSettings: null,
    recruiterLoading: true,
    handlerSettings: null,
    handlerLoading: true,
    isSimulating: false,
    simulationInfo: undefined,
    effectiveRole: undefined,
    isDarkMode: true,
    toggleTheme: () => {},
    recruiterFilters: { dateRange: 'lastMonthCal' },
    overviewFilters: { dateRange: 'lastMonthCal' },
    setOverviewDateRange: () => {},
    setOverviewRegionId: () => {},
    setRecruiterDateRange: () => {},
    setRecruiterRegionId: () => {},
    setRecruiterId: () => {},
    lastDrilldownUrl: '',
    setLastDrilldownUrl: () => {},
    containerWidth: null,
    setContainerWidth: () => {},
  });

export const useOperationsDashboardContext = () =>
  useContext(OperationsDashboardContext);

/* ─── Provider ─── */

export const OperationsDashboardProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { data: currentUser } = useCurrentUser();
  const userRole = currentUser?.role?.value as UserRole | undefined;

  // Load Ops Dashboard settings
  const { data: opsData, loading: opsLoading } = useQuery(
    GET_APP_SETTINGS_QUERY,
    { variables: { input: { settingsTab: 'ops-dashboard' } } }
  );

  const opsSettings: IOpsDashboardSettings = useMemo(() => {
    if (!opsData?.getAppSettings?.settingsJson) return DEFAULT_OPS_SETTINGS;
    return {
      ...DEFAULT_OPS_SETTINGS,
      ...(opsData.getAppSettings
        .settingsJson as Partial<IOpsDashboardSettings>),
    };
  }, [opsData]);

  // Load Phlebotomist Dashboard settings
  const { data: phleboData, loading: phlebotomistLoading } = useQuery(
    GET_APP_SETTINGS_QUERY,
    { variables: { input: { settingsTab: 'phlebotomist-dashboard' } } }
  );

  const phlebotomistSettings = useMemo(
    () =>
      (phleboData?.getAppSettings?.settingsJson as Record<string, unknown>) ??
      null,
    [phleboData]
  );

  // Load Recruiter Dashboard settings
  const { data: recruiterData, loading: recruiterLoading } = useQuery(
    GET_APP_SETTINGS_QUERY,
    { variables: { input: { settingsTab: 'recruiter-dashboard' } } }
  );

  const recruiterSettings = useMemo(
    () =>
      (recruiterData?.getAppSettings?.settingsJson as Record<
        string,
        unknown
      >) ?? null,
    [recruiterData]
  );

  // Load Handler Dashboard settings
  const { data: handlerData, loading: handlerLoading } = useQuery(
    GET_APP_SETTINGS_QUERY,
    { variables: { input: { settingsTab: 'handler-dashboard' } } }
  );

  const handlerSettings = useMemo(
    () =>
      (handlerData?.getAppSettings?.settingsJson as Record<string, unknown>) ??
      null,
    [handlerData]
  );

  // Simulation logic
  const simulationSettings = useMemo(() => {
    if (
      !opsSettings.simulateRole ||
      opsSettings.simulateRole === UserRole.Admin
    )
      return null;
    return {
      simulateRole: opsSettings.simulateRole,
      simulateUserId: opsSettings.simulateUserId || null,
    };
  }, [opsSettings]);

  const isSimulating = !!simulationSettings;

  const { data: simulatedUserData } = useGetUsersListQuery({
    variables: {
      input: {
        filter: { ids: [simulationSettings?.simulateUserId || ''] },
        take: 1,
      },
    },
    skip: !isSimulating || !simulationSettings?.simulateUserId,
  });

  const simulatedUser = useMemo(
    () => simulatedUserData?.getUsersList?.data?.[0] || null,
    [simulatedUserData]
  );

  const effectiveRole =
    isSimulating && userRole === UserRole.Admin
      ? simulationSettings!.simulateRole
      : userRole;

  const simulationInfo: ISimulationInfo | undefined = useMemo(
    () =>
      isSimulating
        ? {
            role: simulationSettings!.simulateRole,
            userName: simulatedUser
              ? `${simulatedUser.firstName} ${simulatedUser.lastName}`
              : undefined,
            officeName: simulatedUser?.assignedOffice?.name || undefined,
          }
        : undefined,
    [isSimulating, simulationSettings, simulatedUser]
  );

  // Theme toggle (persisted in localStorage)
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const stored = localStorage.getItem('ops-dashboard-theme');
    return stored !== 'light';
  });

  const toggleTheme = useCallback(() => {
    setIsDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem('ops-dashboard-theme', next ? 'dark' : 'light');
      return next;
    });
  }, []);

  // Drilldown tab — remember last loaded URL so re-entering preserves state
  const [lastDrilldownUrl, setLastDrilldownUrlRaw] = useState<string>('');
  const setLastDrilldownUrl = useCallback((url: string) => {
    setLastDrilldownUrlRaw(url);
  }, []);

  // Recruiter tab filters — initialized from URL, synced back on change
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const isRecruiterTab = pathname === '/dashboards/operations/recruiter';
  const [recruiterFilters, setRecruiterFilters] = useState<IRecruiterFilters>(
    () => recruiterFiltersFromUrl(searchParams)
  );
  const isSyncingRef = useRef(false);
  useEffect(() => {
    if (!isRecruiterTab) return;
    if (isSyncingRef.current) return;
    const newParams = recruiterFiltersToUrl(recruiterFilters);
    if (searchParams.toString() !== newParams.toString()) {
      isSyncingRef.current = true;
      setSearchParams(newParams, { replace: true });
      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    }
  }, [isRecruiterTab, recruiterFilters, searchParams, setSearchParams]);

  const setRecruiterDateRange = useCallback(
    (dateRange: DateRangeType, customRange?: { start: Date; end: Date }) => {
      setRecruiterFilters((prev) => ({
        ...prev,
        dateRange,
        customDateRange: customRange,
      }));
    },
    []
  );

  const setRecruiterRegionId = useCallback((regionId: string | undefined) => {
    setRecruiterFilters((prev) => ({
      ...prev,
      regionId,
      recruiterId: undefined,
    }));
  }, []);

  const setRecruiterId = useCallback((recruiterId: string | undefined) => {
    setRecruiterFilters((prev) => ({
      ...prev,
      recruiterId,
    }));
  }, []);

  // Overview tab filters — initialized from URL, synced back on change
  const isOverviewTab = pathname === '/dashboards/operations/overview';
  const [overviewFilters, setOverviewFilters] = useState<IOverviewFilters>(
    () => {
      const base: IOverviewFilters = {
        dateRange: DEFAULT_RECRUITER_DATE_RANGE,
      };
      const dr = searchParams.get('dateRange');
      if (dr && VALID_DATE_RANGES.has(dr)) base.dateRange = dr as DateRangeType;
      if (base.dateRange === 'custom') {
        const cs = searchParams.get('customStart');
        const ce = searchParams.get('customEnd');
        if (cs && ce) {
          const s = new Date(cs);
          const e = new Date(ce);
          if (!isNaN(s.getTime()) && !isNaN(e.getTime()))
            base.customDateRange = { start: s, end: e };
        }
      }
      const rid = searchParams.get('regionId');
      if (rid) base.regionId = rid;
      return base;
    }
  );
  useEffect(() => {
    if (!isOverviewTab) return;
    if (isSyncingRef.current) return;
    const newParams = new URLSearchParams();
    if (overviewFilters.dateRange !== DEFAULT_RECRUITER_DATE_RANGE)
      newParams.set('dateRange', overviewFilters.dateRange);
    if (
      overviewFilters.dateRange === 'custom' &&
      overviewFilters.customDateRange
    ) {
      newParams.set(
        'customStart',
        overviewFilters.customDateRange.start.toISOString()
      );
      newParams.set(
        'customEnd',
        overviewFilters.customDateRange.end.toISOString()
      );
    }
    if (overviewFilters.regionId)
      newParams.set('regionId', overviewFilters.regionId);
    if (searchParams.toString() !== newParams.toString()) {
      isSyncingRef.current = true;
      setSearchParams(newParams, { replace: true });
      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    }
  }, [isOverviewTab, overviewFilters, searchParams, setSearchParams]);

  const setOverviewDateRange = useCallback(
    (dateRange: DateRangeType, customRange?: { start: Date; end: Date }) => {
      setOverviewFilters((prev) => ({
        ...prev,
        dateRange,
        customDateRange: customRange,
      }));
    },
    []
  );

  const setOverviewRegionId = useCallback((regionId: string | undefined) => {
    setOverviewFilters((prev) => ({
      ...prev,
      regionId,
    }));
  }, []);

  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const value: IOperationsDashboardContextValue = useMemo(
    () => ({
      opsSettings,
      opsLoading,
      phlebotomistSettings,
      phlebotomistLoading,
      recruiterSettings,
      recruiterLoading,
      handlerSettings,
      handlerLoading,
      isSimulating,
      simulationInfo,
      effectiveRole,
      isDarkMode,
      toggleTheme,
      recruiterFilters,
      setRecruiterDateRange,
      setRecruiterRegionId,
      setRecruiterId,
      overviewFilters,
      setOverviewDateRange,
      setOverviewRegionId,
      lastDrilldownUrl,
      setLastDrilldownUrl,
      containerWidth,
      setContainerWidth,
    }),
    [
      opsSettings,
      opsLoading,
      phlebotomistSettings,
      phlebotomistLoading,
      recruiterSettings,
      recruiterLoading,
      handlerSettings,
      handlerLoading,
      isSimulating,
      simulationInfo,
      effectiveRole,
      isDarkMode,
      toggleTheme,
      recruiterFilters,
      setRecruiterDateRange,
      setRecruiterRegionId,
      setRecruiterId,
      overviewFilters,
      setOverviewDateRange,
      setOverviewRegionId,
      lastDrilldownUrl,
      setLastDrilldownUrl,
      containerWidth,
      setContainerWidth,
    ]
  );

  return (
    <OperationsDashboardContext.Provider value={value}>
      {children}
    </OperationsDashboardContext.Provider>
  );
};
